import { AlertTriangle, ShieldAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { RemakeValidation, RemakeStatus } from '@/lib/api/amazon-listing.types'

interface RemakeValidationBannerProps {
  status: RemakeStatus
  validation?: RemakeValidation
  autoTruncatedFields?: string[]
}

export function RemakeValidationBanner({ status, validation, autoTruncatedFields }: RemakeValidationBannerProps) {
  const { t } = useTranslation()
  const violations = validation?.violations ?? []
  const critical = violations.filter(v => v.severity === 'critical')
  const warnings = violations.filter(v => v.severity === 'warning')
  const truncatedCount = autoTruncatedFields?.length ?? 0

  if (status !== 'validation_failed' && status !== 'verification_required' && truncatedCount === 0) {
    return null
  }

  const isCritical = status === 'validation_failed'
  const isVerification = status === 'verification_required'
  const tone = isCritical ? 'red' : 'amber'
  const Icon = isCritical ? ShieldAlert : AlertTriangle

  const titleKey = isCritical
    ? 'remake.validation.failedTitle'
    : isVerification
      ? 'remake.validation.verificationTitle'
      : 'remake.validation.autoTruncatedTitle'

  return (
    <div className={bannerCls(tone)} role="alert">
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tone === 'red' ? 'text-red-500' : 'text-amber-500'}`} />
        <div className="flex-1 min-w-0 space-y-2">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {t(titleKey)}
          </h4>
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            {t(`remake.validation.${isCritical ? 'failedDescription' : isVerification ? 'verificationDescription' : 'autoTruncatedDescription'}`,
              { count: critical.length || truncatedCount })}
          </p>
          {critical.length > 0 && (
            <ul className="list-disc pl-5 space-y-1 text-xs text-red-700 dark:text-red-300">
              {critical.slice(0, 8).map((v, i) => (
                <li key={`${v.code}-${v.field}-${i}`}>
                  <span className="font-medium">{v.field}:</span> {v.message}
                </li>
              ))}
              {critical.length > 8 && (
                <li className="text-neutral-500 italic">{t('remake.validation.moreViolations', { count: critical.length - 8 })}</li>
              )}
            </ul>
          )}
          {warnings.length > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('remake.validation.warningCount', { count: warnings.length })}
            </p>
          )}
          {truncatedCount > 0 && (
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('remake.validation.autoTruncatedFields', { fields: autoTruncatedFields?.join(', ') })}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function bannerCls(tone: 'red' | 'amber'): string {
  const base = 'rounded-xl border p-4'
  if (tone === 'red') {
    return `${base} bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800`
  }
  return `${base} bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800`
}
